import clsx from 'clsx';
import React, { CSSProperties, createRef, RefObject, useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Toast, ToastPosition, useToasts } from '~/stores/use-toasts/useToasts';
import { CONVERSATION_TOAST } from '~/views/alfred/research/use-notification-toast/useNotificationToast';

import styles from './ToastsLayout.module.scss';

const wrapperPaddingX = 20;
export const offsetBetweenToasts = 20;
export const toastTransitionDuration = 300;
export const transitionClass = 'toast';

const SCOPES_TO_CLEAR = [CONVERSATION_TOAST];

const absolutePositions = {
  [ToastPosition.TopLeft]: { x: 'left', y: 'top' },
  [ToastPosition.Top]: { x: 'middle', y: 'top' },
  [ToastPosition.TopRight]: { x: 'right', y: 'top' },
  [ToastPosition.BottomLeft]: { x: 'left', y: 'bottom' },
  [ToastPosition.Bottom]: { x: 'middle', y: 'bottom' },
  [ToastPosition.BottomRight]: { x: 'right', y: 'bottom' },
} as const;

const getToastLayoutClass = (position: ToastPosition) => {
  const ap = absolutePositions[position];
  if (ap.x === 'middle') {
    return ap.y === 'top' ? styles.toastVMiddleTop : styles.toastVMiddleBottom;
  }
  return ap.x === 'left' ? styles.toastHLeft : styles.toastHRight;
};

const getToastPositionStyle = (
  position: ToastPosition,
  offsetY: number
): CSSProperties => {
  const ap = absolutePositions[position];
  const base: CSSProperties = {
    ['--toast-offset-y' as string]: `${offsetY}px`,
  };
  if (ap.x === 'left') {
    base.left = wrapperPaddingX;
  } else if (ap.x === 'right') {
    base.right = wrapperPaddingX;
  }
  if (ap.y === 'top') {
    base.top = offsetY;
  } else {
    base.bottom = offsetY;
  }
  return base;
};

export const ToastsLayout = () => {
  const { toasts, clearScopedToasts } = useToasts(state => ({
    toasts: state.toasts,
    clearScopedToasts: state.clearScopedToasts,
  }));
  const refs = useRef<Record<string, RefObject<HTMLDivElement | null>>>({});

  toasts.forEach(t => {
    refs.current[t.id] = refs.current[t.id] || createRef();
  });

  const yOffsets = toasts.map((toast: Toast, toastIndex: number) =>
    toasts.slice(0, toastIndex).reduce((offsetY, t, nodeIndex) => {
      const ref = refs.current[t.id];

      if (ref.current && toast.position === toasts[nodeIndex].position) {
        return offsetY + ref.current.clientHeight + offsetBetweenToasts;
      }

      return offsetY;
    }, 0)
  );

  const onExited = (id: Toast['id']) => () => {
    delete refs.current[id];
  };

  const filteredScopes = SCOPES_TO_CLEAR.filter(scope =>
    toasts.some(toast => toast.scope === scope)
  );

  const handleScopedToastsClose = () => {
    filteredScopes.forEach(scope => clearScopedToasts(scope));
  };

  return (
    <div
      className={clsx(
        styles.toastsWrapper,
        filteredScopes.length && styles.toastsWrapperInteractive
      )}
      data-testid="toasts-wrapper"
      onClick={
        filteredScopes.length ? () => handleScopedToastsClose() : undefined
      }
    >
      <TransitionGroup component={null}>
        {toasts.map((toast, toastIndex) => (
          <CSSTransition
            key={toast.id}
            id={toast.id}
            timeout={toastTransitionDuration}
            classNames={transitionClass}
            nodeRef={refs.current[toast.id]}
            onExited={onExited(toast.id)}
          >
            <div
              key={toast.id}
              ref={refs.current[toast.id]}
              className={clsx(
                styles.toast,
                getToastLayoutClass(toast.position)
              )}
              style={getToastPositionStyle(
                toast.position,
                yOffsets[toastIndex]
              )}
              data-testid="global-toast"
            >
              {toast.content}
            </div>
          </CSSTransition>
        ))}
      </TransitionGroup>
    </div>
  );
};
